import { ConfirmDialog } from "components/ConfirmDialog";
import { EmbeddedDocumentPreparationUrlDialog } from "components/Submission/EmbeddedSignature/EmbeddedDocumentPreparationUrlDialog";
import { RejectionRoutingDialog } from "components/Submission/RejectionRoutingDialog";
import { ReviewerManagementDialog } from "components/Submission/ReviewerManagementDialog";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { SignerDialog } from "components/Submission/SignerDialog";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { ReviewMode, SignatureType } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";

type ReviewerAndSignerDialogsProps = {
  facilityId: string;
  reviewerDialogOpen: boolean;
  onCloseReviewerDialog: () => void;
  reviewers: Reviewer[];
  reviewMode: ReviewMode;
  peerReviewRequired: boolean;
  onSaveReviewers: (
    nextReviewers: Reviewer[],
    nextReviewMode: ReviewMode,
    nextPeerReviewRequired?: boolean
  ) => void;
  signerDialogOpen: boolean;
  onCloseSignerDialog: () => void;
  signer: SubmissionReviewerSigner | null;
  signatureType: SignatureType;
  onChangeSigner: (signer: SubmissionReviewerSigner | null) => void;
  onSaveSigner: (signer: SubmissionReviewerSigner) => void;
  reviewersToConfirm: { reviewers: Reviewer[]; reviewMode: ReviewMode } | null;
  onCloseReviewersToConfirm: () => void;
  onConfirmReviewersToConfirm: () => void;
  signerToConfirm: SubmissionReviewerSigner | null;
  onCloseSignerToConfirm: () => void;
  onConfirmSignerToConfirm: () => void;
};

function ReviewerAndSignerDialogs(props: ReviewerAndSignerDialogsProps) {
  return (
    <>
      {props.reviewerDialogOpen && (
        <ReviewerManagementDialog
          open={props.reviewerDialogOpen}
          onClose={props.onCloseReviewerDialog}
          initialReviewers={props.reviewers}
          initialReviewMode={props.reviewMode}
          showEncampStaffPeerReview
          initialPeerReviewRequired={props.peerReviewRequired}
          submissionFacilityIds={[props.facilityId]}
          onSave={props.onSaveReviewers}
        />
      )}

      {props.signerDialogOpen && (
        <SignerDialog
          open={props.signerDialogOpen}
          onClose={props.onCloseSignerDialog}
          initialSigner={props.signer}
          signatureType={props.signatureType}
          submissionFacilityIds={[props.facilityId]}
          // Lifts each edit so validation sees what is typed, not only what was saved.
          onChange={props.onChangeSigner}
          onSave={props.onSaveSigner}
        />
      )}

      {props.reviewersToConfirm && (
        <ConfirmDialog
          open={!!props.reviewersToConfirm}
          onClose={props.onCloseReviewersToConfirm}
          onConfirm={props.onConfirmReviewersToConfirm}
          header="Add this person as a reviewer and remove them as the signer?"
          info="This person is currently set as the signer for this submission."
          confirmText="Yes, add as reviewer"
          cancelText="No, keep as signer"
        />
      )}

      {props.signerToConfirm && (
        <ConfirmDialog
          open={!!props.signerToConfirm}
          onClose={props.onCloseSignerToConfirm}
          onConfirm={props.onConfirmSignerToConfirm}
          header="Add this person as the signer and remove them as a reviewer?"
          info="This person is already assigned to review this document."
          confirmText="Yes, add as signer"
          cancelText="No, keep as reviewer"
        />
      )}
    </>
  );
}

type ReviewerPreparationDialogsProps = ReviewerAndSignerDialogsProps & {
  submissionId: string;
  saving: boolean;
  sending: boolean;
  hasUnsavedChanges: boolean;
  preparationDialogOpen: boolean;
  onClosePreparationDialog: () => void;
  rejectionRoutingOpen: boolean;
  onCloseRejectionRouting: () => void;
  advanceConfirmOpen: boolean;
  onCloseAdvanceConfirm: () => void;
  onConfirmAdvance: () => void;
  finalApprovalReviewers: Reviewer[] | null;
  onCloseFinalApproval: () => void;
  onConfirmFinalApproval: () => void;
};

export function ReviewerPreparationDialogs(props: ReviewerPreparationDialogsProps) {
  return (
    <>
      <ReviewerAndSignerDialogs {...props} />

      {props.preparationDialogOpen && (
        <EmbeddedDocumentPreparationUrlDialog
          open={props.preparationDialogOpen}
          onClose={props.onClosePreparationDialog}
          submissionId={props.submissionId}
          additionalRefetchQueries={["GetSubmissionStepper"]}
        />
      )}

      {props.rejectionRoutingOpen && (
        <RejectionRoutingDialog
          open={props.rejectionRoutingOpen}
          onClose={props.onCloseRejectionRouting}
          submissionId={props.submissionId}
          additionalRefetchQueries={["GetSubmissionStepper"]}
        />
      )}

      {props.advanceConfirmOpen && (
        <ConfirmDialog
          open={props.advanceConfirmOpen}
          onClose={props.onCloseAdvanceConfirm}
          onConfirm={props.onConfirmAdvance}
          header="No reviewers assigned"
          info="This submission has no reviewers, so it will skip review and move straight to Processing. You can return it to In Progress afterward if needed."
          confirmText="Move to Processing"
          cancelText="Cancel"
        />
      )}

      {props.finalApprovalReviewers && (
        <ConfirmDialog
          open={props.finalApprovalReviewers !== null}
          loading={props.saving}
          onClose={props.onCloseFinalApproval}
          onConfirm={props.onConfirmFinalApproval}
          header="Complete review and submit?"
          info="This approval completes the review and moves the submission into processing. This can't be undone."
          confirmText="Approve & Submit"
          cancelText="Cancel"
        />
      )}

      <UnfinishedChangesPrompt
        when={
          props.hasUnsavedChanges && !props.sending && !props.saving && !props.preparationDialogOpen
        }
        header="Close without saving?"
        confirmText="Yes, close without saving"
        info="You have unsaved reviewer, signer, or signature changes. Leaving will discard them."
      />
    </>
  );
}
